import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import axios from 'axios';
import { API } from '../../../contexts/AuthContext';
import { confirmDialog, errorAlert } from '../../../helpers/alerts';
import MapaPlano, { MarcadorCamera } from '../MapaPlano';

// Mapas: envia a planta / foto aerea e posiciona as cameras nela.
// Na lista da direita, clicar numa camera fora do mapa e depois no mapa coloca
// ela ali; no mapa, arrastar move e clicar seleciona (direcao e remover).

const MAX_MB = 15;

function enviarFormulario(metodo, url, dados, arquivo) {
  const fd = new FormData();
  Object.entries(dados).forEach(([k, v]) => fd.append(k, v ?? ''));
  if (arquivo) fd.append('imagem', arquivo);
  return axios({ method: metodo, url, data: fd, headers: { 'Content-Type': 'multipart/form-data' } });
}

function validarArquivo(arquivo) {
  if (!arquivo) return '';
  if (!/^image\/(jpeg|png|webp)$/.test(arquivo.type)) return 'Use uma imagem JPG, PNG ou WEBP.';
  if (arquivo.size > MAX_MB * 1024 * 1024) return `A imagem pode ter no máximo ${MAX_MB} MB.`;
  return '';
}

export default function MapasAba({ onAlterou }) {
  const [mapas, setMapas] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [gravadores, setGravadores] = useState([]);
  const [filiais, setFiliais] = useState([]);
  const [erro, setErro] = useState('');
  const [novo, setNovo] = useState({ nome: '', filial: '', arquivo: null });
  const [enviando, setEnviando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);

  const carregar = useCallback(async () => {
    try {
      const [rm, rc, rg] = await Promise.all([
        axios.get(`${API}/cameras/mapas/admin`),
        axios.get(`${API}/cameras/cameras`),
        axios.get(`${API}/cameras/gravadores`),
      ]);
      setMapas(rm.data?.data?.mapas || []);
      setCameras(rc.data?.data?.cameras || []);
      setGravadores(rg.data?.data?.gravadores || []);
    } catch (e) {
      setErro(e.response?.data?.message || 'Não foi possível carregar os mapas.');
    }
  }, []);

  useEffect(() => {
    carregar();
    axios.get(`${API}/cameras/filiais`)
      .then((res) => setFiliais(res.data?.data || []))
      .catch(() => setFiliais([]));
  }, [carregar]);

  const criar = async (e) => {
    e.preventDefault();
    const problema = validarArquivo(novo.arquivo);
    if (problema) { setErro(problema); return; }
    setEnviando(true);
    setErro('');
    try {
      const ordem = mapas.reduce((m, x) => Math.max(m, x.ordem), 0) + 10;
      const res = await enviarFormulario('post', `${API}/cameras/mapas`, { nome: novo.nome, filial: novo.filial, ordem }, novo.arquivo);
      setNovo({ nome: '', filial: '', arquivo: null });
      await carregar();
      onAlterou();
      // Ja abre o editor para posicionar as cameras.
      if (res.data?.data?.id) setEditandoId(res.data.data.id);
    } catch (err) {
      setErro(err.response?.data?.message || 'Não foi possível criar o mapa.');
    } finally {
      setEnviando(false);
    }
  };

  const excluir = async (m) => {
    const r = await confirmDialog({
      title: `Excluir o mapa "${m.nome}"?`,
      text: 'A imagem e as posições das câmeras neste mapa são apagadas. As câmeras continuam cadastradas.',
      confirmButtonText: 'Excluir',
    });
    if (!r.isConfirmed) return;
    try {
      await axios.delete(`${API}/cameras/mapas/${m.id}`);
      await carregar();
      onAlterou();
    } catch (e) {
      errorAlert({ title: 'Erro', text: e.response?.data?.message || 'Não foi possível excluir.' });
    }
  };

  const mapaEditado = mapas.find((m) => m.id === editandoId);
  if (mapaEditado) {
    return (
      <EditorMapa
        key={mapaEditado.id}
        mapa={mapaEditado}
        cameras={cameras}
        gravadores={gravadores}
        filiais={filiais}
        onVoltar={() => setEditandoId(null)}
        onSalvou={async () => { await carregar(); onAlterou(); }}
      />
    );
  }

  return (
    <>
      {erro ? <div className="feedback-banner feedback-banner--error" style={{ marginBottom: 12 }}>{erro}</div> : null}

      <form onSubmit={criar} className="cameras-config__topo" style={{ flexWrap: 'wrap' }}>
        <input
          type="text"
          className="cameras-config__select"
          placeholder="Nome do mapa (ex.: Loja 03 - térreo)"
          maxLength={100}
          value={novo.nome}
          onChange={(e) => setNovo((n) => ({ ...n, nome: e.target.value }))}
          style={{ flex: 1, minWidth: 200 }}
        />
        <select
          className="cameras-config__select"
          value={novo.filial}
          onChange={(e) => setNovo((n) => ({ ...n, filial: e.target.value }))}
          title="Filial"
        >
          <option value="">Sem filial</option>
          {filiais.map((f) => <option key={f.codigo} value={f.codigo}>{f.codigo} - {f.nome}</option>)}
        </select>
        <label className="btn btn-secondary btn-sm mb-0" title={`Planta, foto aérea ou print do mapa (JPG, PNG ou WEBP, até ${MAX_MB} MB)`}>
          <i className="bi bi-image me-1" />
          {novo.arquivo ? novo.arquivo.name.slice(0, 28) : 'Escolher imagem'}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            onChange={(e) => setNovo((n) => ({ ...n, arquivo: e.target.files?.[0] || null }))}
          />
        </label>
        <button type="submit" className="btn btn-primary btn-sm" disabled={!novo.nome.trim() || !novo.arquivo || enviando}>
          <i className="bi bi-plus-lg me-1" /> {enviando ? 'Enviando…' : 'Criar mapa'}
        </button>
      </form>

      {mapas.length === 0 ? (
        <div className="empty-state">
          <span>
            Nenhum mapa ainda. Envie a planta da loja, uma foto aérea ou um print do Google Maps
            e depois posicione as câmeras nela.
          </span>
        </div>
      ) : (
        <div className="table-shell">
          <table className="portal-table">
            <thead>
              <tr>
                <th>Mapa</th>
                <th style={{ width: 110 }}>Filial</th>
                <th style={{ width: 150 }}>Câmeras no mapa</th>
                <th style={{ width: 130 }} />
              </tr>
            </thead>
            <tbody>
              {mapas.map((m) => (
                <tr key={m.id}>
                  <td className="cell-strong">{m.nome}</td>
                  <td>{m.filial || '—'}</td>
                  <td>{m.itens.length}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button type="button" className="btn btn-primary btn-sm" onClick={() => setEditandoId(m.id)} title="Posicionar câmeras">
                        <i className="bi bi-pin-map" />
                      </button>
                      <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => excluir(m)} title="Excluir">
                        <i className="bi bi-trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function EditorMapa({
  mapa, cameras, gravadores, filiais, onVoltar, onSalvou,
}) {
  const [dados, setDados] = useState({ nome: mapa.nome, filial: mapa.filial || '', ordem: mapa.ordem });
  const [novaImagem, setNovaImagem] = useState(null);
  const [itens, setItens] = useState(() => mapa.itens.map((it) => ({ ...it })));
  const [selecionada, setSelecionada] = useState(null); // camera_id no mapa
  const [colocando, setColocando] = useState(null); // camera_id esperando o clique no mapa
  const [busca, setBusca] = useState('');
  const [soDaFilial, setSoDaFilial] = useState(!!mapa.filial);
  const [alterado, setAlterado] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const planoRef = useRef(null);
  const arrasto = useRef(null);

  const gravadoresPorId = useMemo(() => new Map(gravadores.map((g) => [g.id, g])), [gravadores]);
  const camerasPorId = useMemo(() => new Map(cameras.map((c) => [c.id, c])), [cameras]);
  const noMapa = useMemo(() => new Set(itens.map((it) => it.camera_id)), [itens]);
  const nomeDe = (c) => c.nome_exibicao || c.nome || `CH ${c.canal}`;

  const lista = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return cameras
      .filter((c) => !soDaFilial || !dados.filial || gravadoresPorId.get(c.gravador_id)?.filial === dados.filial)
      .filter((c) => !termo || `${nomeDe(c)} ${gravadoresPorId.get(c.gravador_id)?.nome || ''}`.toLowerCase().includes(termo))
      .sort((a, b) => String(gravadoresPorId.get(a.gravador_id)?.nome).localeCompare(String(gravadoresPorId.get(b.gravador_id)?.nome), 'pt-BR')
        || a.canal - b.canal);
  }, [cameras, busca, soDaFilial, dados.filial, gravadoresPorId]);

  const mudarItens = (fn) => { setItens(fn); setAlterado(true); };

  const colocarEm = (x, y) => {
    if (!colocando) { setSelecionada(null); return; }
    const id = colocando;
    mudarItens((lst) => [...lst.filter((it) => it.camera_id !== id), { camera_id: id, x, y, direcao: null }]);
    setColocando(null);
    setSelecionada(id);
  };

  // Arrastar o marcador: mede a camada da imagem e converte para %.
  const iniciarArrasto = (cameraId) => (e) => {
    e.preventDefault();
    arrasto.current = { cameraId, x0: e.clientX, y0: e.clientY, moveu: false };
    setSelecionada(cameraId);
    setColocando(null);
  };

  useEffect(() => {
    const mover = (e) => {
      const a = arrasto.current;
      const plano = planoRef.current;
      if (!a || !plano) return;
      if (!a.moveu && Math.hypot(e.clientX - a.x0, e.clientY - a.y0) < 4) return;
      a.moveu = true;
      const r = plano.getBoundingClientRect();
      const x = Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100));
      const y = Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100));
      setItens((lst) => lst.map((it) => (it.camera_id === a.cameraId ? { ...it, x, y } : it)));
      setAlterado(true);
    };
    const soltar = () => { arrasto.current = null; };
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
    return () => {
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
    };
  }, []);

  const itemSel = itens.find((it) => it.camera_id === selecionada) || null;
  const cameraSel = itemSel ? camerasPorId.get(itemSel.camera_id) : null;
  const mudarSel = (campos) => mudarItens((lst) => lst.map((it) => (it.camera_id === selecionada ? { ...it, ...campos } : it)));

  const salvar = async () => {
    const problema = validarArquivo(novaImagem);
    if (problema) { setErro(problema); return; }
    setSalvando(true);
    setErro('');
    try {
      await enviarFormulario('put', `${API}/cameras/mapas/${mapa.id}`, dados, novaImagem);
      await axios.put(`${API}/cameras/mapas/${mapa.id}/itens`, { itens });
      setAlterado(false);
      setNovaImagem(null);
      await onSalvou();
    } catch (e) {
      setErro(e.response?.data?.message || 'Não foi possível salvar o mapa.');
    } finally {
      setSalvando(false);
    }
  };

  const voltar = async () => {
    if (alterado) {
      const r = await confirmDialog({
        title: 'Sair sem salvar?',
        text: 'As mudanças neste mapa serão perdidas.',
        confirmButtonText: 'Sair sem salvar',
      });
      if (!r.isConfirmed) return;
    }
    onVoltar();
  };

  // Previa da imagem nova antes de salvar.
  const [previa, setPrevia] = useState(null);
  useEffect(() => {
    if (!novaImagem) { setPrevia(null); return undefined; }
    const url = URL.createObjectURL(novaImagem);
    setPrevia(url);
    return () => URL.revokeObjectURL(url);
  }, [novaImagem]);

  return (
    <div className="mapa-editor">
      {erro ? <div className="feedback-banner feedback-banner--error">{erro}</div> : null}

      <div className="cameras-config__topo" style={{ flexWrap: 'wrap', marginBottom: 0 }}>
        <button type="button" className="btn btn-secondary btn-sm" onClick={voltar}>
          <i className="bi bi-arrow-left me-1" /> Mapas
        </button>
        <input
          type="text"
          className="cameras-config__select"
          maxLength={100}
          value={dados.nome}
          onChange={(e) => { setDados((d) => ({ ...d, nome: e.target.value })); setAlterado(true); }}
          style={{ flex: 1, minWidth: 180 }}
          aria-label="Nome do mapa"
        />
        <select
          className="cameras-config__select"
          value={dados.filial}
          onChange={(e) => { setDados((d) => ({ ...d, filial: e.target.value })); setAlterado(true); }}
          title="Filial"
        >
          <option value="">Sem filial</option>
          {filiais.map((f) => <option key={f.codigo} value={f.codigo}>{f.codigo} - {f.nome}</option>)}
          {dados.filial && !filiais.some((f) => f.codigo === dados.filial) && (
            <option value={dados.filial}>{dados.filial}</option>
          )}
        </select>
        <label className="btn btn-secondary btn-sm mb-0" title="Trocar a imagem (as posições são mantidas)">
          <i className="bi bi-image me-1" /> {novaImagem ? 'Imagem nova escolhida' : 'Trocar imagem'}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            onChange={(e) => { setNovaImagem(e.target.files?.[0] || null); setAlterado(true); }}
          />
        </label>
        <button type="button" className="btn btn-primary btn-sm" onClick={salvar} disabled={salvando || !dados.nome.trim()}>
          <i className="bi bi-check-lg me-1" /> {salvando ? 'Salvando…' : 'Salvar mapa'}
        </button>
      </div>

      <small className="cell-muted">
        {colocando
          ? `Clique no mapa onde fica "${nomeDe(camerasPorId.get(colocando))}".`
          : 'Escolha uma câmera na lista e clique no mapa para colocá-la. Arraste para mover; clique para ajustar a direção.'}
      </small>

      <div className="mapa-editor__corpo">
        <div className={`mapa-editor__plano ${colocando ? 'mapa-editor__plano--colocando' : ''}`}>
          <MapaPlano ref={planoRef} url={previa || mapa.imagem_url} onClickPlano={colocarEm}>
            {itens.map((it) => {
              const c = camerasPorId.get(it.camera_id);
              if (!c) return null;
              return (
                <MarcadorCamera
                  key={it.camera_id}
                  item={it}
                  nome={nomeDe(c)}
                  status="ok"
                  selecionado={selecionada === it.camera_id}
                  mostrarNome
                  titulo={`${nomeDe(c)} — arraste para mover`}
                  onPointerDown={iniciarArrasto(it.camera_id)}
                />
              );
            })}
          </MapaPlano>
        </div>

        <aside className="mapa-editor__lado">
          {itemSel && cameraSel ? (
            <div className="mapa-editor__sel">
              <strong>{nomeDe(cameraSel)}</strong>
              <small className="cell-muted">
                {gravadoresPorId.get(cameraSel.gravador_id)?.nome} · CH {cameraSel.canal}
              </small>
              <label className="mapa-cameras__opcao">
                <input
                  type="checkbox"
                  checked={itemSel.direcao !== null && itemSel.direcao !== undefined}
                  onChange={(e) => mudarSel({ direcao: e.target.checked ? 0 : null })}
                />
                Mostrar para onde aponta
              </label>
              {itemSel.direcao !== null && itemSel.direcao !== undefined ? (
                <label className="portal-field">
                  <span>Direção: {itemSel.direcao}°</span>
                  <input
                    type="range"
                    min={0}
                    max={359}
                    value={itemSel.direcao}
                    onChange={(e) => mudarSel({ direcao: Number(e.target.value) })}
                  />
                </label>
              ) : null}
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => { mudarItens((lst) => lst.filter((it) => it.camera_id !== selecionada)); setSelecionada(null); }}
              >
                <i className="bi bi-x-lg me-1" /> Tirar do mapa
              </button>
            </div>
          ) : null}

          <input
            type="search"
            className="cameras-config__select"
            placeholder="Buscar câmera"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          {dados.filial ? (
            <label className="mapa-cameras__opcao">
              <input type="checkbox" checked={soDaFilial} onChange={(e) => setSoDaFilial(e.target.checked)} />
              Só equipamentos da filial {dados.filial}
            </label>
          ) : null}

          <div className="mapa-editor__lista">
            {lista.map((c) => {
              const posicionada = noMapa.has(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  className={[
                    'mapa-editor__camera',
                    colocando === c.id ? 'mapa-editor__camera--colocando' : '',
                    selecionada === c.id ? 'mapa-editor__camera--sel' : '',
                  ].join(' ')}
                  onClick={() => {
                    if (posicionada) { setSelecionada(c.id); setColocando(null); } else { setColocando(c.id); setSelecionada(null); }
                  }}
                  title={posicionada ? 'Já está no mapa — clique para selecionar' : 'Clique e depois clique no mapa'}
                >
                  <i className={`bi ${posicionada ? 'bi-geo-alt-fill' : 'bi-plus-circle'}`} />
                  <span>
                    {nomeDe(c)}
                    <small className="cell-muted"> · {gravadoresPorId.get(c.gravador_id)?.nome} CH {c.canal}</small>
                  </span>
                </button>
              );
            })}
            {lista.length === 0 ? <small className="cell-muted">Nenhuma câmera encontrada.</small> : null}
          </div>
        </aside>
      </div>
    </div>
  );
}
